import { ComponentProps, FC, ReactNode } from 'react';
import { Tooltip } from 'antd';
import { isMobile, isTablet } from 'react-device-detect';
import { TbInfoCircle } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

export type TooltipProps = ComponentProps<typeof Tooltip>;

export interface SmallTooltipProps {
    align?: TooltipProps['align'];
    children?: ReactNode;
    chromeless?: boolean;
    title?: string | ReactNode;
    /** Display only shortcuts handled by the tooltip's owning surface. */
    shortcut?: ReactNode;
    placement?:
        | 'top'
        | 'left'
        | 'right'
        | 'bottom'
        | 'topLeft'
        | 'topRight'
        | 'bottomLeft'
        | 'bottomRight'
        | 'leftTop'
        | 'leftBottom'
        | 'rightTop'
        | 'rightBottom';
    padding?: string;
    margin?: string;
    style?: React.CSSProperties;
    overlayStyle?: React.CSSProperties;
    disabled?: boolean;
    showInfo?: boolean;
    trigger?: TooltipProps['trigger'];
}

interface SmallTooltipContentProps {
    children: ReactNode;
    showInfo?: boolean;
}

export const SmallTooltipContent: FC<SmallTooltipContentProps> = ({ children, showInfo = true }) => {
    const { token } = useAntDesignTheme();

    return (
        <small style={{ verticalAlign: '0.05rem', color: token.colorText }}>
            {showInfo && (
                <TbInfoCircle
                    style={{
                        color: token.colorTextTertiary,
                        verticalAlign: '-0.1rem',
                        marginRight: '0.375rem',
                    }}
                />
            )}
            {children}
        </small>
    );
};

const SmallTooltip: FC<SmallTooltipProps> = ({
    align,
    children,
    chromeless = false,
    title,
    shortcut,
    placement,
    padding,
    margin,
    style,
    overlayStyle,
    disabled,
    showInfo = true,
    trigger,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const hasTitle = title !== undefined && title !== null && title !== '';

    return (
        <Tooltip
            align={align}
            trigger={trigger}
            mouseEnterDelay={0.5} // Might remove this, we'll see
            color={chromeless ? 'transparent' : token.colorBgContainer}
            autoAdjustOverflow
            placement={placement}
            styles={{
                root: {
                    padding: padding ?? 0,
                    maxWidth: chromeless ? 'none' : undefined,
                    ...overlayStyle,
                    display: isMobile || isTablet || disabled ? 'none' : undefined,
                },
                body: {
                    background: chromeless ? 'transparent' : undefined,
                    border: chromeless ? 0 : `1px solid ${token.colorBorder}`,
                    boxShadow: chromeless ? 'none' : undefined,
                    margin: margin ?? 0,
                    padding: chromeless ? 0 : '0.25rem 0.5rem 0.125rem',
                },
            }}
            title={
                hasTitle ? (
                    chromeless ? (
                        title
                    ) : (
                        <SmallTooltipContent showInfo={showInfo}>
                            {title}
                            {shortcut ? (
                                <span
                                    style={{
                                        color: token.colorTextTertiary,
                                        marginInlineStart: '0.5rem',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {shortcut}
                                </span>
                            ) : null}
                        </SmallTooltipContent>
                    )
                ) : undefined
            }
            arrow={false}
            style={{ display: disabled ? 'none' : undefined, ...style }}
        >
            {children}
        </Tooltip>
    );
};

export default SmallTooltip;
